import React, { useEffect, useState } from "react";
import {
  VStack,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  HStack,
  Button,
  Box,
  Divider,
  Textarea,
  Text,
} from "@chakra-ui/react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import Map, { Source, Marker, NavigationControl, ScaleControl } from "react-map-gl";

interface AddViewNameProps {
  deviceType: string;
  deviceName: string;
  setDeviceName: (v: string) => void;
  sensorDepth?: number;
  setSensorDepth: (v: number) => void;
  flowSensorSize?: number;
  setFlowSensorSize: (v: number) => void;

  location: { latitude: number; longitude: number } | null;
  registerLoading: boolean;
  onRegisterLocation: () => void;
  onBack: () => void;
  onApply: () => Promise<any> | void;
}

enum FlowSensorSize {
  NO_FLOW_SENSOR = 0,
  THREE_QUARTER = 1, // represents 3/4"
  ONE = 2, // represents 1"
  ONE_AND_HALF = 3, // represents 1.5"
  TWO = 4, // represents 2"
}

const flowSensorSizeOptions = [
  { value: FlowSensorSize.NO_FLOW_SENSOR, label: "No flow sensor" },
  { value: FlowSensorSize.THREE_QUARTER, label: "3/4'" },
  { value: FlowSensorSize.ONE, label: "1'" },
  { value: FlowSensorSize.ONE_AND_HALF, label: "1.5'" },
  { value: FlowSensorSize.TWO, label: "2'" },
];

const AddViewName: React.FC<AddViewNameProps> = ({
  deviceType,
  deviceName,
  setDeviceName,
  sensorDepth,
  setSensorDepth,
  flowSensorSize,
  setFlowSensorSize,
  location,
  registerLoading,
  onRegisterLocation,
  onBack,
  onApply,
}) => {
  const [viewState, setViewState] = useState({
    longitude: location?.longitude ?? 0,
    latitude: location?.latitude ?? 0,
    zoom: location ? 14 : 2,
  });

  useEffect(() => {
    if (!location) onRegisterLocation();
  }, []);

  useEffect(() => {
    if (location) {
      setViewState({ longitude: location.longitude, latitude: location.latitude, zoom: 12 });
    }
  }, [location]);

  return (
    <VStack spacing={6} align="stretch" p={4}>
      <Box width="350px" height="250px" alignSelf="center">
        <Map
          {...viewState}
          onMove={(evt) => setViewState(evt.viewState)}
          mapLib={import("mapbox-gl")}
          mapStyle="mapbox://styles/mapbox/satellite-v9"
          mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
          style={{ width: "100%", height: "100%" }}
        >
          <Source
            id="mapbox-dem"
            type="raster-dem"
            url="mapbox://mapbox.mapbox-terrain-dem-v1"
            tileSize={512}
            maxzoom={14}
          />
          <NavigationControl />
          <ScaleControl />
          {location && (
            <Marker latitude={location.latitude} longitude={location.longitude} anchor="bottom">
              <Box w="20px" h="20px" bg="teal.500" borderRadius="full" border="2px solid white" />
            </Marker>
          )}
        </Map>
      </Box>

      <Heading size="md" textAlign="center">
        <Trans>Adding {deviceType.toLowerCase()} </Trans>
      </Heading>

      <FormControl>
        <FormLabel>
          <Trans>Name (Optional)</Trans>
        </FormLabel>
        <Input
          value={deviceName}
          onChange={(e) => setDeviceName(e.target.value)}
          placeholder={t`My Device`}
        />
      </FormControl>

      {deviceType === "SENSOR" && (
        <FormControl>
          <FormLabel>
            <Trans>Sensor Depth (cm)</Trans>
          </FormLabel>
          <Select
            placeholder={t`Fully submerged`}
            value={sensorDepth?.toString() ?? ""}
            onChange={(e) => setSensorDepth(+e.target.value)}
          >
            {[5, 10, 15].map((d) => (
              <option key={d} value={d}>
                {d}cm over ground
              </option>
            ))}
          </Select>
        </FormControl>
      )}

      {deviceType === "VALVE_WITH_FLOW_SENSOR" && (
        <FormControl isRequired>
          <FormLabel>
            <Trans>Flow sensor</Trans>
          </FormLabel>
          <Select
            placeholder={t`No flow sensor`}
            value={flowSensorSize?.toString() ?? ""}
            onChange={(e) => setFlowSensorSize(+e.target.value)}
          >
            {flowSensorSizeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </FormControl>
      )}

      <Divider />

      <HStack spacing={4}>
        <Button flex={1} variant="outline" onClick={onBack}>
          <Trans>Back</Trans>
        </Button>
        <Button
          flex={1}
          colorScheme="teal"
          onClick={onApply}
          isLoading={registerLoading}
          isDisabled={!location || registerLoading}
        >
          <Trans>Apply</Trans>
        </Button>
      </HStack>
    </VStack>
  );
};

export default AddViewName;
